import { Box, ListItem, Stack, TextField, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { FC, ReactElement, useEffect } from 'react';
import { DetectPattern } from '@/gen/api/securefile';
import { usePlainStateFormUtils } from '@/components/input/usePlainStateFormUtils';
import { useZSliceUnderContext } from '@/utils/zustand-form/hooks';
import { dpSchema } from './InfoTypeEditor.types';
import type { FormToken } from '@/utils/zustand-form/types';
import { CollectEditsMetadata, CollectEditsProtocol } from '@/utils/zustand-form/ext/collectEdits';

const DetectPatternLine: FC<{
  parentToken: FormToken,
  className?: string,
  r_key: string,
  prevData: DetectPattern,
  actionButton: ReactElement,
}> = ({ parentToken, className, r_key, prevData, actionButton }) => {
  const zustandUtils = useZSliceUnderContext<DetectPattern>({
    parentToken: parentToken ?? null,
    pathFragment: r_key,
    prevData: prevData,
    stageInit: () => ({ ...prevData }),
  });
  const { states, setStates, installProtocolMetadata } = zustandUtils

  useEffect(() => { setStates(v => ({...v, prevData, stage: {...prevData}, errors:{}, touched:{} })) }, [prevData, setStates]);
  const { trySubmitValidate, fieldProps } = usePlainStateFormUtils(states, setStates, dpSchema);

  useEffect(() => {
    return installProtocolMetadata<CollectEditsMetadata>(CollectEditsProtocol.metadataKey, {
      triggerValidate: async () => { 
        try {
          await trySubmitValidate() 
        } catch (error) {
          throw new Error('Validation error', { cause: error })
        }  
      }
    })
  }, [installProtocolMetadata, trySubmitValidate])

  return (
    <ListItem className={className ?? (Object.keys(states.touched).length > 0 ? 'dirty' : undefined)}>
      <Stack className="line-box" direction="row" spacing={4} >
        <TextField label="Name"  variant="standard" size="small" {...fieldProps('name')} multiline/>
        <TextField variant="standard" size="small" type="number" sx={{ width: 60, '& p.MuiFormHelperText-root.Mui-error': { width: 200 } }}
          label={
            <Stack direction="row" alignItems="start">
              Score
              <Tooltip title="A matching score indicates how likely the matching content is actually of this Info Type. The score may be modified by the Context Rules below, if any. A final score of 0.5 or above is deemed that the matching content is of this Info Type.">
                <Box role="button">
                  <InfoIcon sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
                </Box>
              </Tooltip>
            </Stack>}
          {...fieldProps('score', (x: any) => {
            if (!x) { return undefined; }
            let n = +x;
            if (Number.isNaN(n)) { return 0; }
            n = parseFloat(n.toFixed(2));
            return (
                n < 0 ? 0
              : n > 1 ? 1
              : n);
          })}
          inputProps={{ step: 0.01, max: 1, min: 0, }}
        />
        <TextField label="Regular Expression" variant="standard" size="small" {...fieldProps('regex')} multiline />
      </Stack>
      {actionButton}
    </ListItem>
  );
};

export default DetectPatternLine;